import { byId } from "../domUtils";
import type { PrototypeRuntime, Root } from "../prototypeTypes";
import { isValidUrl, safeFileName } from "../validation";
import { maxSourceAttachmentBytes, MAX_SOURCE_ATTACHMENTS, MAX_SOURCE_LINKS, validateSourceAttachment } from '../../../domain/sourceAttachmentPolicy';
import { getRecoverableTurnstileToken } from '../turnstileRuntime';
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';

export type UploadedAttachment = {
  name: string;
  objectKey: string;
  submissionId: string;
  contentType: string;
  size: number;
};

export type CollectedIdeaPayload = {
  links: Array<{ url: string; label?: string }>;
  attachments: UploadedAttachment[];
};

const MAX_LINKS = MAX_SOURCE_LINKS;
const MAX_ATTACHMENTS = MAX_SOURCE_ATTACHMENTS;

function sourceAlertId(listId: string): string {
	return listId.startsWith('idea') ? 'ideaFormAlert' : 'aiCreateFormAlert';
}

function paintSourceFeedback(root: Root, listId: string, message: string, kind: 'error' | 'success' = 'error'): false {
	return paintOperationFeedback(root, sourceAlertId(listId), message, kind);
}

function collectLinks(root: Root, listId: string): Array<{ url: string; label?: string }> {
  const list = byId(root, listId);
  if (!list) return [];
  const rows = list.querySelectorAll<HTMLElement>(".idea-link-row");
  const out: Array<{ url: string; label?: string }> = [];
  rows.forEach((row) => {
    const url = (row.querySelector<HTMLInputElement>(".idea-link-url")?.value ?? "").trim();
    const label = (row.querySelector<HTMLInputElement>(".idea-link-desc")?.value ?? "").trim();
    if (!url && !label) return; // empty row
    if (!isValidUrl(url)) {
		paintSourceFeedback(root, listId, `Only https links are supported: ${url || "(empty)"}`);
      return;
    }
    if (out.length >= MAX_LINKS) return;
    out.push({ url, label: label || undefined });
  });
  return out;
}

function collectAttachments(root: Root, listId: string): UploadedAttachment[] {
  const list = byId(root, listId);
  if (!list) return [];
  const items = list.querySelectorAll<HTMLElement>("[data-uploaded-key]");
  const out: UploadedAttachment[] = [];
  items.forEach((el) => {
    const objectKey = el.getAttribute("data-uploaded-key");
    if (!objectKey) return;
    if (out.length >= MAX_ATTACHMENTS) return;
    out.push({
      name: el.getAttribute("data-uploaded-name") || "file",
      objectKey,
      submissionId: el.getAttribute("data-uploaded-submission-id") || "",
      contentType: el.getAttribute("data-uploaded-type") || "application/octet-stream",
      size: Number(el.getAttribute("data-uploaded-size") || 0),
    });
  });
  return out;
}

export function collectIdeaFormPayload(root: Root): CollectedIdeaPayload {
  return {
    links: collectLinks(root, "ideaLinkList"),
    attachments: collectAttachments(root, "ideaAttachmentList"),
  };
}

export function collectAiFormPayload(root: Root): CollectedIdeaPayload {
  return {
    links: collectLinks(root, "aiLinkList"),
    attachments: collectAttachments(root, "aiAttachmentList"),
  };
}

function addLinkRow(root: Root, listId: string, addBtnId: string) {
  const list = byId(root, listId);
  const addBtn = byId(root, addBtnId);
  if (!list) return;
  const existing = list.querySelectorAll(".idea-link-row").length;
  if (existing >= MAX_LINKS) {
	paintSourceFeedback(root, listId, `At most ${MAX_LINKS} links.`);
    return;
  }
  const row = document.createElement("div");
  row.className = "idea-link-row";
  const position = existing + 1;
  row.innerHTML = `<input type="url" class="input idea-link-url" maxlength="2048" autocomplete="off" placeholder="https://..." aria-label="Reference URL ${position}">
    <input type="text" class="input idea-link-desc" maxlength="64" autocomplete="off" placeholder="Link description" aria-label="Reference link description ${position}">
    <button type="button" class="btn-icon idea-link-remove" aria-label="Remove reference link ${position}">×</button>`;
  list.appendChild(row);
  row.querySelector(".idea-link-remove")?.addEventListener("click", () => row.remove());
  if (existing + 1 >= MAX_LINKS && addBtn) {
    (addBtn as HTMLButtonElement).style.display = "none";
  }
}

export function addIdeaLink(root: Root) {
  addLinkRow(root, "ideaLinkList", "ideaAddLinkBtn");
}

export function addAiLink(root: Root) {
  addLinkRow(root, "aiLinkList", "aiAddLinkBtn");
}

/**
 * Check whether any attachment in the given list is still uploading
 * (has the `attachment-row--uploading` class and no `data-uploaded-key`).
 * Used to block Continue / Submit while an upload is in-flight.
 */
export function hasUploadingAttachments(root: Root, listId: string): boolean {
  const list = byId(root, listId);
  if (!list) return false;
  return !!list.querySelector(".attachment-row--uploading");
}

function addAttachmentPlaceholder(root: Root, listId: string, inputId: string, addBtnId: string) {
  const list = byId(root, listId);
  const addBtn = byId(root, addBtnId);
  if (!list) return;
  const existing = list.querySelectorAll("[data-uploaded-key]").length;
  if (existing >= MAX_ATTACHMENTS) {
	paintSourceFeedback(root, listId, `At most ${MAX_ATTACHMENTS} attachments.`);
    return;
  }
  const input = byId(root, inputId) as HTMLInputElement | null;
  if (!input) {
	paintSourceFeedback(root, listId, "Attachment input is not ready. Please reopen the form.");
    return;
  }
  input?.click();
  if (existing + 1 >= MAX_ATTACHMENTS && addBtn) {
    (addBtn as HTMLButtonElement).style.display = "none";
  }
}

export function addIdeaAttachment(root: Root) {
  addAttachmentPlaceholder(root, "ideaAttachmentList", "ideaAttachmentInput", "ideaAddAttachmentBtn");
}

export function addAiAttachment(root: Root) {
  addAttachmentPlaceholder(root, "aiAttachmentList", "aiAttachmentInput", "aiAddAttachmentBtn");
}

function appendAttachmentRow(root: Root, runtime: PrototypeRuntime, listId: string, addBtnId: string, attachment: UploadedAttachment) {
  const list = byId(root, listId);
  if (!list) return;
  const row = document.createElement("div");
  row.className = "attachment-row";
  row.setAttribute("data-uploaded-key", attachment.objectKey);
  row.setAttribute("data-uploaded-submission-id", attachment.submissionId);
  row.setAttribute("data-uploaded-name", attachment.name);
  row.setAttribute("data-uploaded-type", attachment.contentType);
  row.setAttribute("data-uploaded-size", String(attachment.size));
  const sizeKb = Math.max(1, Math.round(attachment.size / 1024));
  row.innerHTML = `<span class="attachment-name">${safeFileName(attachment.name)}</span>
    <span class="attachment-size">${sizeKb} KB</span>
    <button type="button" class="btn-icon attachment-remove" aria-label="Remove">×</button>`;
  row.querySelector(".attachment-remove")?.addEventListener("click", async () => {
    const objectKey = row.getAttribute("data-uploaded-key");
    row.remove();
    // Delete the R2 object so we don't leave orphaned files.
    if (objectKey) {
      try {
        await runtime.api.deleteAttachment(objectKey);
      } catch (err) {
        console.warn("[attachment] R2 deletion failed", err);
      }
    }
    // Restore the Add button if we're below the limit now.
    const currentCount = list.querySelectorAll("[data-uploaded-key]").length;
    if (currentCount < MAX_ATTACHMENTS) {
      const addBtn = byId(root, addBtnId) as HTMLButtonElement | null;
      if (addBtn) addBtn.style.display = "";
    }
  });
  list.appendChild(row);
}

function appendUploadingRow(root: Root, listId: string, fileName: string): HTMLElement | null {
  const list = byId(root, listId);
  if (!list) return null;
  const row = document.createElement("div");
  row.className = "attachment-row attachment-row--uploading";
  row.innerHTML = `<span class="attachment-name">${safeFileName(fileName)}</span>
    <span class="attachment-size">Uploading...</span>`;
  list.appendChild(row);
  return row;
}

async function handleAttachmentFile(
  event: Event,
  root: Root,
  runtime: PrototypeRuntime,
  listId: string,
  addBtnId: string,
  context: "community" | "ai",
) {
  // Use event.target (the file input that fired the change) instead of
  // event.currentTarget (which is `root` in the delegated listener).
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  const validation = validateSourceAttachment(context, file);
  if (!validation.ok) {
    if (validation.reason === "type") {
		paintSourceFeedback(root, listId, "Unsupported file type. Supported files: .doc, .docx, .pdf, .txt, .md, .markdown");
    } else {
		paintSourceFeedback(root, listId, `File too large. Max ${Math.round(maxSourceAttachmentBytes(context) / (1024 * 1024))} MB.`);
    }
    return;
  }
  const addBtn = byId(root, addBtnId) as HTMLButtonElement | null;
	clearOperationFeedback(root, sourceAlertId(listId));
  if (addBtn) {
    addBtn.disabled = true;
    addBtn.setAttribute("aria-busy", "true");
  }
  const uploading = appendUploadingRow(root, listId, file.name);
  try {
		const turnstileToken = await getRecoverableTurnstileToken(runtime, `uploads-presign-${context}`);
    // P4.5: presign 需要 purpose + submissionId（防跨 purpose / 跨 actor 滥用）
    // submissionId 用客户端生成的 UUID（ attachments 在 idea/job 创建前上传，
    // 后续 idea/job 提交时把 objectKey + submissionId 一起带给服务端做落库绑定）
    const submissionId = crypto.randomUUID();
    const attachment = await runtime.api.uploadAttachment(file, context, submissionId, turnstileToken);
    uploading?.remove();
    appendAttachmentRow(root, runtime, listId, addBtnId, attachment);
    const list = byId(root, listId);
    if (list && addBtn) {
      const count = list.querySelectorAll("[data-uploaded-key]").length;
      if (count >= MAX_ATTACHMENTS) (addBtn as HTMLButtonElement).style.display = "none";
    }
	// The completed attachment row (file name + size) is the AI Create success
	// state. Keep Community's existing confirmation, but do not add a second,
	// wizard-global message that can outlive AI Create step 2.
	if (context === 'community') paintSourceFeedback(root, listId, 'File attached.', 'success');
  } catch (error) {
    uploading?.remove();
    console.warn(`[${context}] attachment upload failed`, error);
	paintSourceFeedback(root, listId, 'Upload failed. Please try again.');
  } finally {
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.removeAttribute("aria-busy");
    }
  }
}

export async function handleIdeaAttachment(event: Event, root: Root, runtime: PrototypeRuntime) {
  await handleAttachmentFile(event, root, runtime, "ideaAttachmentList", "ideaAddAttachmentBtn", "community");
}

export async function handleAiAttachment(event: Event, root: Root, runtime: PrototypeRuntime) {
  await handleAttachmentFile(event, root, runtime, "aiAttachmentList", "aiAddAttachmentBtn", "ai");
}
